import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, Camera, Loader2, Rocket } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fileUrl = (p) => `${BACKEND_URL}/api/files/${p}`;
const inp = "w-full bg-secondary/40 border border-border rounded-md px-3 py-3 text-base outline-none focus:border-[#17A2B8]";
const TYPES = [["voiture", "Voiture", "distance"], ["utilitaire", "Utilitaire / VUL", "distance"], ["camion", "Camion", "distance"], ["engin", "Engin", "engine_hours"], ["machine", "Machine", "engine_hours"], ["materiel", "Matériel", "engine_hours"], ["bateau", "Bateau", "distance"]];
const ROAD = ["voiture", "utilitaire", "camion"];
const ICONS = ["car", "van", "truck", "excavator", "equipment", "boat"];

export const AssistedUnitModal = ({ open, onClose, companies, devices, onCreated }) => {
  const [busy, setBusy] = useState(false);
  const [ocr, setOcr] = useState("");
  const [f, setF] = useState({ company_id: "", name: "", make: "", model: "", type: "camion", plate: "", park_number: "", serial: "", internal_id: "", usage_metric: "distance", icon: "truck", counter_value: "", device_id: "", _conf: null });
  const [photos, setPhotos] = useState([]);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const road = ROAD.includes(f.type);
  const identityOk = f.name.trim() && (road ? f.plate.trim() : (f.plate.trim() || f.park_number.trim() || f.serial.trim() || f.internal_id.trim()));

  if (!open) return null;

  const selType = (t) => { const d = TYPES.find((x) => x[0] === t); setF((s) => ({ ...s, type: t, usage_metric: d ? d[2] : s.usage_metric })); };
  const addPhoto = (ref, kind) => { if (ref) setPhotos((p) => [...p.filter((x) => x.kind !== kind), { ...ref, kind }]); };

  const onVehiclePhoto = async (e, kind) => {
    const file = e.target.files?.[0]; e.target.value = ""; if (!file) return;
    setOcr(kind);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = (await api.post("/units/ocr/vehicle", fd)).data;
      addPhoto(r.file, kind);
      setF((s) => ({ ...s, make: r.make || s.make, model: r.model || s.model, plate: (r.plate && ROAD.includes(r.unit_type || s.type)) ? r.plate : s.plate, type: r.unit_type || s.type, usage_metric: (TYPES.find((x) => x[0] === (r.unit_type || s.type)) || [])[2] || s.usage_metric, park_number: r.park_number || s.park_number, serial: r.serial || s.serial, icon: r.suggested_icon || s.icon, _conf: r.confidence }));
    } catch { toast.info("Lecture auto impossible — saisissez les champs."); }
    setOcr("");
  };
  const onMeterPhoto = async (e) => {
    const file = e.target.files?.[0]; e.target.value = ""; if (!file) return;
    setOcr("meter");
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("metric", f.usage_metric);
      const r = (await api.post("/installations/ocr/meter", fd)).data;
      addPhoto(r.file, "meter"); if (r.value) set("counter_value", String(r.value));
    } catch { toast.info("Compteur illisible — saisissez la valeur."); }
    setOcr("");
  };

  const create = async () => {
    if (!f.company_id || !identityOk) { toast.error("Société, nom et plaque/identifiant requis"); return; }
    setBusy(true);
    try {
      await api.post("/units/assisted", { company_id: f.company_id, name: f.name, make: f.make || null, model: f.model || null, type: f.type, plate: f.plate || null, park_number: f.park_number || null, serial: f.serial || null, internal_id: f.internal_id || null, usage_metric: f.usage_metric, icon: f.icon, counter_value: f.counter_value === "" ? null : Number(f.counter_value), device_id: f.device_id || null, photos });
      toast.success("Unité créée");
      onCreated && onCreated(); onClose();
      setF({ company_id: "", name: "", make: "", model: "", type: "camion", plate: "", park_number: "", serial: "", internal_id: "", usage_metric: "distance", icon: "truck", counter_value: "", device_id: "", _conf: null }); setPhotos([]);
    } catch (e) { toast.error(e?.response?.data?.detail || "Création impossible"); }
    setBusy(false);
  };

  const PhotoBtn = ({ kind, label, onChange }) => {
    const ph = photos.find((p) => p.kind === kind);
    return (
      <div>
        <label className="flex items-center justify-center gap-2 w-full min-h-[48px] rounded-md border-2 border-dashed border-border cursor-pointer hover:bg-secondary/40 text-sm font-semibold" data-testid={`au-photo-${kind}`}>
          {ocr === kind ? <Loader2 className="w-5 h-5 animate-spin" /> : <Camera className="w-5 h-5 text-[#17A2B8]" />} {label}
          <input type="file" accept="image/*,.heic,.heif" capture="environment" className="hidden" onChange={onChange} />
        </label>
        {ph && <img src={fileUrl(ph.path)} alt="" className="w-14 h-14 rounded object-cover border border-border mt-2" data-testid={`au-thumb-${kind}`} />}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[1100] bg-black/60 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="bg-background w-full sm:max-w-md sm:rounded-xl rounded-t-2xl max-h-[94vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="assisted-unit-modal">
        <div className="sticky top-0 bg-background border-b border-border px-4 py-3 flex items-center justify-between z-10">
          <h2 className="text-base font-bold flex items-center gap-2"><Rocket className="w-4 h-4 text-[#F5821F]" /> Création assistée par photos</h2>
          <button onClick={onClose} data-testid="au-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 space-y-3">
          <select className={inp} value={f.company_id} onChange={(e) => set("company_id", e.target.value)} data-testid="au-company">
            <option value="">— Société —</option>
            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <div className="grid grid-cols-3 gap-2">
            <PhotoBtn kind="front" label="Avant" onChange={(e) => onVehiclePhoto(e, "front")} />
            <PhotoBtn kind="back" label="Arrière" onChange={(e) => onVehiclePhoto(e, "back")} />
            <PhotoBtn kind="meter" label="Compteur" onChange={onMeterPhoto} />
          </div>
          {f._conf != null && <p className="text-xs text-muted-foreground">Reconnaissance — confiance {Math.round(f._conf * 100)}% (modifiable ci-dessous)</p>}
          <input className={inp} value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="Nom de l'unité" data-testid="au-name" />
          <div className="grid grid-cols-2 gap-2">
            <input className={inp} value={f.make} onChange={(e) => set("make", e.target.value)} placeholder="Marque (ex. Peugeot)" data-testid="au-make" />
            <input className={inp} value={f.model} onChange={(e) => set("model", e.target.value)} placeholder="Modèle (ex. Partner)" data-testid="au-model" />
          </div>
          <select className={inp} value={f.type} onChange={(e) => selType(e.target.value)} data-testid="au-type">
            {TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          {road ? (
            <input className={inp} value={f.plate} onChange={(e) => set("plate", e.target.value.toUpperCase())} placeholder="Immatriculation" data-testid="au-plate" />
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <input className={inp} value={f.plate} onChange={(e) => set("plate", e.target.value.toUpperCase())} placeholder="Plaque (optionnel)" data-testid="au-plate" />
              <input className={inp} value={f.park_number} onChange={(e) => set("park_number", e.target.value)} placeholder="N° de parc" data-testid="au-park" />
              <input className={inp} value={f.serial} onChange={(e) => set("serial", e.target.value)} placeholder="N° de série" data-testid="au-serial" />
              <input className={inp} value={f.internal_id} onChange={(e) => set("internal_id", e.target.value)} placeholder="Identifiant interne" data-testid="au-internal" />
            </div>
          )}
          <div className="flex gap-2">
            <button onClick={() => set("usage_metric", "distance")} data-testid="au-metric-distance" className={`flex-1 rounded-md py-2 text-sm font-semibold border ${f.usage_metric === "distance" ? "bg-[#17A2B8] text-white border-[#17A2B8]" : "border-border"}`}>Kilométrage</button>
            <button onClick={() => set("usage_metric", "engine_hours")} data-testid="au-metric-hours" className={`flex-1 rounded-md py-2 text-sm font-semibold border ${f.usage_metric === "engine_hours" ? "bg-[#17A2B8] text-white border-[#17A2B8]" : "border-border"}`}>Heures moteur</button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input className={inp} inputMode="decimal" value={f.counter_value} onChange={(e) => set("counter_value", e.target.value)} placeholder={f.usage_metric === "engine_hours" ? "Heures (ex. 3218.7)" : "Km (ex. 125430)"} data-testid="au-counter" />
            <select className={inp} value={f.icon} onChange={(e) => set("icon", e.target.value)} data-testid="au-icon">
              {ICONS.map((i) => <option key={i} value={i}>{i}</option>)}
            </select>
          </div>
          <select className={inp} value={f.device_id} onChange={(e) => set("device_id", e.target.value)} data-testid="au-device">
            <option value="">Boîtier : Aucun (en attente de pose)</option>
            {(devices || []).filter((d) => !d.vehicle_id).map((d) => <option key={d.id} value={d.id}>{(d.name || d.model)} · {d.imei}</option>)}
          </select>
          <button onClick={create} disabled={busy || !f.company_id || !identityOk} data-testid="au-create" className="w-full bg-[#F5821F] text-white font-bold py-3.5 rounded-md flex items-center justify-center gap-2 disabled:opacity-50">
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Rocket className="w-5 h-5" />} Créer l'unité
          </button>
        </div>
      </div>
    </div>
  );
};
